import React, { useState, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { Drawer, Button, Space, Spin, Alert, Empty } from 'antd';
import { ArrowLeftOutlined, PlusOutlined } from '@ant-design/icons';
import ListItemsDataTable from './ListItemsDataTable.component';
import DynamicFormSP from './DynamicFormSP.component';
import ApiErrorAlert from '../common/ApiErrorAlert';
import {
    loadUserDashboardDto,
    listUserDashboardsForUser,
    listUserDashboards,
    createUserDashboard,
    updateUserDashboard,
    deleteUserDashboard,
    getUserDashboardDetails,
    listUserDashboardsByIds,
} from '../../actions/userDashboardActions';

const SUMMARY_PROPS = ['name', 'dashboardId', 'isDefault', 'displayOrder', 'isActive'];
const FIELDS_TO_HIDE = ['Id', 'ApplicationUserId', 'CreatedAt', 'UpdatedAt'];

export default function UserDashboardManagementDrawer({ open, onClose, applicationUserId }) {
    const { t } = useTranslation();
    const dispatch = useDispatch();
    const [isListView, setIsListView] = useState(true);
    const [selectedEntityId, setSelectedEntityId] = useState(null);
    const [listFilter, setListFilter] = useState({ query: '', page: 1, limit: 10, filter: 'name' });

    /** Per-user list only — avoids clobbering by SingleEntitySelect (listUserDashboards /all). */
    const {
        dashboards: userDashboards = [],
        loading: loadingUserDashboards,
        error: userDashboardError,
    } = useSelector((s) => s.userDashboardByUser);
    const limit = listFilter.limit || 10;
    const totalCount = userDashboards.length;
    const totalPages = Math.max(1, Math.ceil(totalCount / limit));
    const currentPage = Math.min(listFilter.page || 1, totalPages);
    const pagedData = userDashboards.slice((currentPage - 1) * limit, currentPage * limit);
    const {
        userDashboardDto: entityDtoConfig,
        loading: loadingDto,
        error: dtoError,
    } = useSelector((s) => s.userDashboardDto);
    const { userDashboard: detailsRecord, error: detailsError } = useSelector((s) => s.userDashboardDetails) || {};
    const detailsLoading = useSelector((s) => s.userDashboardDetails?.loading);

    useEffect(() => {
        if (open && applicationUserId) {
            dispatch(loadUserDashboardDto());
            dispatch(listUserDashboardsForUser(applicationUserId));
        }
    }, [open, applicationUserId, dispatch]);

    const refreshList = () => applicationUserId && dispatch(listUserDashboardsForUser(applicationUserId));

    const handleCreate = () => {
        setSelectedEntityId(null);
        setIsListView(false);
    };

    const handleEdit = (id) => {
        setSelectedEntityId(id);
        setIsListView(false);
    };

    const handleDelete = (id) => {
        dispatch(deleteUserDashboard(id)).then(() => refreshList());
    };

    const handleBack = () => {
        setSelectedEntityId(null);
        setIsListView(true);
        refreshList();
    };

    const createEntityWithUser = (data) => {
        if (!applicationUserId) return;
        dispatch(createUserDashboard({ ...data, applicationUserId })).then(() => refreshList());
    };

    if (!applicationUserId) return null;

    const headerActions = isListView ? (
        <Space>
            <Button type="primary" size="small" icon={<PlusOutlined />} onClick={handleCreate}>
                {t('Create')} {t('Dashboard')}
            </Button>
        </Space>
    ) : (
        <Button type="text" size="small" icon={<ArrowLeftOutlined />} onClick={handleBack}>
            {t('Back to')} {t('List')}
        </Button>
    );

    return (
        <Drawer
            title={
                <Space style={{ width: '100%', justifyContent: 'space-between' }}>
                    <span>{t('Manage Dashboards')}</span>
                    {headerActions}
                </Space>
            }
            open={open}
            onClose={onClose}
            width={Math.min(840, typeof window !== 'undefined' ? window.innerWidth : 840)}
            destroyOnClose
        >
            {isListView ? (
                loadingDto && !entityDtoConfig ? (
                    <div style={{ padding: 48, textAlign: 'center' }}>
                        <Spin />
                    </div>
                ) : dtoError ? (
                    <ApiErrorAlert error={dtoError} title={t('Could not load form')} />
                ) : !entityDtoConfig ? (
                    <Alert type="warning" showIcon message={t('Could not load form')} />
                ) : (
                    <>
                        <ApiErrorAlert error={userDashboardError} title={t('Error loading dashboards')} />
                        <ListItemsDataTable
                            loading={loadingUserDashboards}
                            dtoConfig={entityDtoConfig}
                            data={pagedData}
                            filter={listFilter}
                            currentPage={currentPage}
                            totalPages={totalPages}
                            totalCount={totalCount}
                            limit={limit}
                            onFilterChange={setListFilter}
                            handleDeleteRow={handleDelete}
                            handleEditForm={handleEdit}
                            getEntityDetails={getUserDashboardDetails}
                            detailsRecord={detailsRecord}
                            detailsLoading={detailsLoading}
                            detailsError={detailsError}
                            enableActionButtons
                            locale={
                                totalCount === 0 && !loadingUserDashboards && !userDashboardError
                                    ? {
                                          emptyText: (
                                              <Empty
                                                  description={t('No dashboards yet. Use Create to add one.')}
                                                  image={Empty.PRESENTED_IMAGE_SIMPLE}
                                              />
                                          ),
                                      }
                                    : undefined
                            }
                        />
                    </>
                )
            ) : (
                <div className="entity-management-form-container" style={{ width: '100%' }}>
                    <DynamicFormSP
                        key={selectedEntityId ?? 'new'}
                        currentEntityId={selectedEntityId}
                        selectedItemIds={selectedEntityId ? [selectedEntityId] : []}
                        defaultEditMode={selectedEntityId ? 'Edit' : 'Create'}
                        primarySearchProp="name"
                        singularEntity="userDashboard"
                        pluralEntity="userDashboards"
                        constantPrefix="USER_DASHBOARD"
                        summaryProps={SUMMARY_PROPS}
                        loadDto={loadUserDashboardDto}
                        createEntity={createEntityWithUser}
                        updateEntity={(id, data) => dispatch(updateUserDashboard(id, data)).then(() => refreshList())}
                        deleteEntity={deleteUserDashboard}
                        getEntityDetails={getUserDashboardDetails}
                        listEntities={listUserDashboards}
                        listEntitiesByIds={listUserDashboardsByIds}
                        isParent
                        fieldsToHide={FIELDS_TO_HIDE}
                        enableCreate
                        enableSearchBar={false}
                        enableActionButtons
                    />
                </div>
            )}
        </Drawer>
    );
}
